/**
 * Upgrade Return Handler Hook
 * Detects when user returns from Stripe checkout and handles state restoration
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getOperationState, clearOperationState } from './useOperationState';
import { stripeApi, billingApi } from '../services/api';

/**
 * Hook to handle return from upgrade flow
 * @param {object} options
 * @param {function} options.onResume - Callback with saved state when resuming operation
 * @param {function} options.onUpgradeSuccess - Callback when upgrade is confirmed successful
 * @param {function} options.onUpgradeCancel - Callback when user cancelled checkout
 * @param {function} options.onSeatsAvailable - Callback when seats are available to assign (passes seat count)
 * @param {boolean} options.autoSync - Auto-sync subscription on success (default: true)
 * @param {boolean} options.checkPendingRequests - Check for pending upgrade requests (default: true)
 */
export function useUpgradeReturn({
  onResume,
  onUpgradeSuccess,
  onUpgradeCancel,
  onSeatsAvailable,
  autoSync = true,
  checkPendingRequests = true,
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [resumedState, setResumedState] = useState(null);
  const [showAssignSeats, setShowAssignSeats] = useState(false);
  const [newSeatsCount, setNewSeatsCount] = useState(0);
  
  // Process return from checkout
  const processReturn = useCallback(async () => {
    const checkout = searchParams.get('checkout');
    const resume = searchParams.get('resume');
    const upgraded = searchParams.get('upgraded');
    
    if (!checkout && !resume && !upgraded) return;
    
    setIsProcessing(true);
    
    try {
      // Get saved operation state
      const savedState = getOperationState();
      
      if (checkout === 'success' || upgraded === 'true') {
        // Successful checkout/upgrade
        console.log('[UpgradeReturn] Processing successful checkout');
        
        let seatInfo = { available_seats: 0 };
        
        // Sync subscription to ensure we have latest status
        if (autoSync) {
          try {
            const syncResult = await stripeApi.syncSubscription();
            console.log('[UpgradeReturn] Sync result:', syncResult.data);
            
            // Check for new seats
            if (syncResult.data?.seats) {
              seatInfo.new_seats = syncResult.data.seats;
            }
          } catch (error) {
            console.warn('[UpgradeReturn] Sync failed, continuing anyway:', error);
          }
        }
        
        // Check for pending upgrade requests and available seats
        if (checkPendingRequests) {
          try {
            const contextResponse = await billingApi.getUpgradeContext('seats');
            const context = contextResponse.data;
            
            seatInfo.available_seats = context?.available_seats || 0;
            seatInfo.purchased_seats = context?.seats_purchased || 0;
            
            // Check if there are pending requests
            const requestsResponse = await billingApi.getPendingUpgradeRequests();
            const pendingRequests = requestsResponse.data?.requests || [];
            
            console.log('[UpgradeReturn] Seat info:', seatInfo, 'Pending requests:', pendingRequests.length);
            
            // If we have available seats and pending requests, show assign modal
            if (seatInfo.available_seats > 0 && pendingRequests.length > 0) {
              setNewSeatsCount(seatInfo.available_seats);
              setShowAssignSeats(true);
              onSeatsAvailable?.(seatInfo.available_seats);
            }
          } catch (error) {
            console.warn('[UpgradeReturn] Failed to check seat status:', error);
          }
        }
        
        // Show success toast
        toast.success('Upgrade successful! 🎉', {
          description: seatInfo.available_seats > 0 
            ? `Your plan has been upgraded with ${seatInfo.available_seats} seat(s) available.`
            : 'Your plan has been upgraded. You can now continue where you left off.',
          duration: 5000,
        });
        
        // Call success callback
        onUpgradeSuccess?.();
        
        // If we have saved state and resume flag, restore it
        if (savedState && (resume === 'true' || savedState.reason === 'upgrade')) {
          console.log('[UpgradeReturn] Restoring saved state:', savedState.type);
          setResumedState(savedState.state);
          onResume?.(savedState);
        }
        
        // Clear the saved state
        clearOperationState();
        
      } else if (checkout === 'cancelled') {
        // User cancelled checkout
        console.log('[UpgradeReturn] Checkout cancelled');
        
        toast.info('Checkout cancelled', {
          description: 'No charges were made. You can try again anytime.',
          duration: 4000,
        });
        
        onUpgradeCancel?.();
        
        // Keep saved state so user can try again
        if (savedState) {
          setResumedState(savedState.state);
          onResume?.(savedState);
        }
      }
      
      // Clean up URL params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('checkout');
      newParams.delete('resume');
      newParams.delete('upgraded');
      setSearchParams(newParams, { replace: true });
      
    } catch (error) {
      console.error('[UpgradeReturn] Error processing return:', error);
      toast.error('Error processing upgrade', {
        description: 'Please refresh the page and check your subscription status.',
      });
    } finally {
      setIsProcessing(false);
    }
  }, [searchParams, setSearchParams, autoSync, checkPendingRequests, onResume, onUpgradeSuccess, onUpgradeCancel, onSeatsAvailable]);
  
  // Process on mount and when search params change
  useEffect(() => {
    processReturn();
  }, [processReturn]);
  
  return {
    isProcessing,
    resumedState,
    hasResumedState: resumedState !== null,
    showAssignSeats,
    setShowAssignSeats,
    newSeatsCount,
  };
}

export default useUpgradeReturn;
